import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import '@ant-design/compatible/assets/index.css';
import { Input } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { toLatitude, toLongitude } from '../../utils/LocationUtils';
import LocationMapPin from '../location_map_pin/LocationMapPin';

require('./LocationAutocomplete.less');

const parseAddressComponents = (addressComponents) => {
	return addressComponents.reduce((acc, ac) => {
		ac.types.forEach((t) => (acc[t] = ac.long_name));
		return acc;
	}, {});
};

const extractCity = (parsedComponents) => {
	return nullSafeGetOrElse(
		'locality',
		parsedComponents,
		nullSafeGetOrElse(
			'postal_town',
			parsedComponents,
			nullSafeGetOrElse(
				'sublocality',
				parsedComponents,
				nullSafeGetOrElse(
					'neighborhood',
					parsedComponents,
					nullSafeGetOrElse(
						'administrative_area_level_3',
						parsedComponents,
						nullSafeGet('administrative_area_level_2', parsedComponents)
					)
				)
			)
		)
	);
};

const buildStreetAddress = (parsedComponents) => {
	const streetNumber = parsedComponents.street_number;
	const route = parsedComponents.route;
	return [streetNumber, route].filter(Boolean).join(' ');
};

export const LocationAutocomplete = (props) => {
	const { placeholder, lat, lon, disabled, onSelect, onInputChange, size, showCoordinateEditor } =
		props;

	const initialStreetAddress1 = nullSafeGetOrElse('value.streetAddress1', props, '');
	const initialStreetAddress2Opt = nullSafeGetOrElse('value.streetAddress2Opt', props, '');

	const [streetAddress2Opt, setStreetAddress2Opt] = useState(initialStreetAddress2Opt);
	const [parsedAddressState, setParsedAddressState] = useState({
		city: nullSafeGetOrElse('value.city', props, ''),
		region: nullSafeGetOrElse('value.region', props, ''),
		postcode: nullSafeGetOrElse('value.postcode', props, ''),
		country: nullSafeGetOrElse('value.country', props, ''),
	});

	// `lat`/`lon` are the coordinates the consumer holds -- this input is controlled for them, and
	// reports a change back through `onSelect` like any other edit to the address.
	const currentLatitude = toLatitude(lat);
	const currentLongitude = toLongitude(lon);
	const hasCoordinates = currentLatitude !== null && currentLongitude !== null;
	// The editor is opt-in rather than inferred from `lat`/`lon`: several consumers already pass
	// those to drive other behaviour, and some of them decorate an empty address value, which this
	// component must not report back over their populated fields.
	const coordinateEditorEnabled = showCoordinateEditor === true && hasCoordinates;

	// Where the address itself geocoded to, which is what "reset the pin" goes back to.
	const [geocodedCoordinates, setGeocodedCoordinates] = useState(null);
	// Clicking a suggestion blurs the input first, so two geocodes are in flight at once -- one for
	// the partial text, one for the chosen description -- and they can resolve out of order. Only the
	// newest request may report, or a stale partial address lands on top of the chosen one.
	const geocodeRequestIdRef = useRef(0);
	// The last address the geocoder produced. Kept so a coordinate-only edit can re-emit the address
	// unchanged: `parsedAddress.streetAddress1` is the geocoder's street line, not the raw input text.
	const [lastParsedAddress, setLastParsedAddress] = useState({
		streetAddress1: initialStreetAddress1,
		streetAddress2Opt: initialStreetAddress2Opt,
		city: nullSafeGetOrElse('value.city', props, ''),
		region: nullSafeGetOrElse('value.region', props, ''),
		postcode: nullSafeGetOrElse('value.postcode', props, ''),
		country: nullSafeGetOrElse('value.country', props, ''),
	});

	const {
		ready,
		value,
		setValue,
		suggestions: { status, data },
		clearSuggestions,
	} = usePlacesAutocomplete({
		requestOptions: {
			types: ['address'],
		},
		debounce: 300,
	});

	useEffect(() => {
		if (initialStreetAddress1 && !value) {
			setValue(initialStreetAddress1, false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const prevPropsRef = useRef({
		streetAddress1: initialStreetAddress1,
		streetAddress2Opt: initialStreetAddress2Opt,
	});

	const streetAddress1FromProps = nullSafeGetOrElse('value.streetAddress1', props, '');
	const streetAddress2OptFromProps = nullSafeGetOrElse('value.streetAddress2Opt', props, '');
	const cityFromProps = nullSafeGetOrElse('value.city', props, '');
	const regionFromProps = nullSafeGetOrElse('value.region', props, '');
	const postcodeFromProps = nullSafeGetOrElse('value.postcode', props, '');
	const countryFromProps = nullSafeGetOrElse('value.country', props, '');

	useEffect(() => {
		if (
			streetAddress1FromProps &&
			streetAddress1FromProps !== prevPropsRef.current.streetAddress1
		) {
			setValue(streetAddress1FromProps, false);
			clearSuggestions();
			prevPropsRef.current.streetAddress1 = streetAddress1FromProps;
		}

		if (streetAddress2OptFromProps !== prevPropsRef.current.streetAddress2Opt) {
			setStreetAddress2Opt(streetAddress2OptFromProps);
			prevPropsRef.current.streetAddress2Opt = streetAddress2OptFromProps;
		}

		setParsedAddressState({
			city: cityFromProps,
			region: regionFromProps,
			postcode: postcodeFromProps,
			country: countryFromProps,
		});
	}, [
		streetAddress1FromProps,
		streetAddress2OptFromProps,
		cityFromProps,
		regionFromProps,
		postcodeFromProps,
		countryFromProps,
		setValue,
		clearSuggestions,
	]);

	/**
	 * `fromSuggestion` distinguishes the user picking an address out of the dropdown from the input
	 * merely losing focus, which re-geocodes whatever is already in the box. It decides which
	 * coordinates this component reports; it is not part of what consumers receive.
	 */
	const processAddress = useCallback(
		(addressText, fromSuggestion?: boolean) => {
			if (!addressText) return;

			geocodeRequestIdRef.current = geocodeRequestIdRef.current + 1;
			const requestId = geocodeRequestIdRef.current;

			getGeocode({ address: addressText })
				.then((results) => {
					if (requestId !== geocodeRequestIdRef.current) {
						return;
					}
					if (results.length > 0) {
						const result = results[0];
						const parsedComponents = parseAddressComponents(result.address_components);
						const latLng = getLatLng(result);

						const parsedAddress = {
							streetAddress1: buildStreetAddress(parsedComponents),
							streetAddress2Opt: streetAddress2Opt,
							city: extractCity(parsedComponents),
							region: parsedComponents.administrative_area_level_1,
							postcode: parsedComponents.postal_code,
							country: parsedComponents.country,
						};

						setParsedAddressState({
							city: parsedAddress.city,
							region: parsedAddress.region,
							postcode: parsedAddress.postcode,
							country: parsedAddress.country,
						});
						setLastParsedAddress(parsedAddress);
						setGeocodedCoordinates(latLng);

						// Picking an address out of the dropdown means "use this address", so its
						// coordinates win. A blur merely re-geocodes what is already in the box, and must
						// not discard a pin the user placed by hand -- unless there is no pin yet.
						const emittedLatLng =
							fromSuggestion === true || !hasCoordinates
								? latLng
								: { lat: currentLatitude, lng: currentLongitude };

						onSelect(addressText, emittedLatLng, parsedAddress);
					}
				})
				.catch((error) => {
					console.log('Geocode error:', error);
				});
		},
		[streetAddress2Opt, onSelect, hasCoordinates, currentLatitude, currentLongitude]
	);

	const handleSelect = useCallback(
		(selectedAddress) => {
			setValue(selectedAddress, false);
			clearSuggestions();
			processAddress(selectedAddress, true);
		},
		[setValue, clearSuggestions, processAddress]
	);

	const handleBlur = useCallback(() => {
		if (value) {
			processAddress(value);
		}
	}, [value, processAddress]);

	// `onSelect` waits on a geocode; `onInputChange` signals at once that the street text was edited.
	const handleChange = useCallback(
		(text) => {
			setValue(text, true);
			if (onInputChange) onInputChange();
		},
		[setValue, onInputChange]
	);

	const handleStreetAddress2OptBlur = useCallback(() => {
		processAddress(value);
	}, [processAddress, value]);

	// Submitting the pin modal is a change to the address like any other, so it reports through the
	// same callback -- the same address, with the coordinates the user chose.
	const handleCoordinatesChange = useCallback(
		({ latitude, longitude }) => {
			onSelect(
				value,
				{ lat: latitude, lng: longitude },
				{ ...lastParsedAddress, streetAddress2Opt }
			);
		},
		[onSelect, value, lastParsedAddress, streetAddress2Opt]
	);

	const cityRegionPostcode = [
		parsedAddressState.city,
		parsedAddressState.region,
		parsedAddressState.postcode,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div>
			<div className="flex flex-row gap-x-4">
				<div
					className="locationAutocomplete__container"
					style={{ minWidth: '50%', position: 'relative' }}
				>
					<Input
						size={size}
						value={value}
						onChange={(e) => handleChange(e.target.value)}
						onBlur={handleBlur}
						placeholder={placeholder}
						className="location-search-input"
						autoComplete="random"
						disabled={disabled || !ready}
					/>

					{status === 'OK' && data.length > 0 ? (
						<div className="locationAutocomplete__dropdownContainer">
							{data.map((suggestion) => (
								<div
									key={suggestion.place_id}
									onClick={() => handleSelect(suggestion.description)}
									className="locationAutocomplete__suggestionItem"
								>
									<span>{suggestion.description}</span>
								</div>
							))}
						</div>
					) : null}
				</div>
				<Input
					disabled={disabled}
					placeholder="PO#, Apt, suite, unit, building, floor, etc."
					value={streetAddress2Opt}
					onChange={(e) => setStreetAddress2Opt(e.target.value)}
					onBlur={handleStreetAddress2OptBlur}
				/>
			</div>

			<div className="flex flex-row gap-x-4">
				<Input disabled placeholder="City" value={cityRegionPostcode} style={{ marginTop: 16 }} />
			</div>

			{coordinateEditorEnabled ? (
				<div className="mt-m">
					<LocationMapPin
						latitude={currentLatitude}
						longitude={currentLongitude}
						addressLatitude={nullSafeGet('lat', geocodedCoordinates)}
						addressLongitude={nullSafeGet('lng', geocodedCoordinates)}
						addressLabel={[value, streetAddress2Opt].filter(Boolean).join(', ')}
						onChange={handleCoordinatesChange}
					/>
				</div>
			) : null}
		</div>
	);
};
